import { Resource } from 'src_common/common/mobx-utils/Resource';
import { makeObservable, observable, computed, action } from 'mobx';
import { Common } from 'src/AppState/Common';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { searchAccountList } from 'src_server/types/accountData';

export type CustomerItemType = searchAccountList.SuccessResponseType['results'][number]['properties'] & { id: number };
export class CustomersListState {
    private readonly customersListResource: Resource<Array<CustomerItemType>>;
    @observable private queryData: Record<string, unknown> | null = null;
    @observable public isSearching = false;

    public static get = AutoWeakMap.create((common: Common) => new CustomersListState(common));

    private constructor(common: Common) {
        this.customersListResource = new Resource(async (): Promise<Array<CustomerItemType>> => {
            if (this.queryData !== null) {
                const response = await common.trpcClient.client.accountData.searchAccountList.mutate(this.queryData);

                if (response.responseStatus === 'success') {
                    const result = response.data.results.map((item) => {
                        return {
                            id: item.id,
                            ...item.properties,
                        };
                    });
                    return result;
                }
            }
            return [];
        });
        makeObservable(this);
    }

    @computed public get isReady(): boolean {
        const resource = this.customersListResource.get();
        return resource.type === 'ready';
    }

    @computed public get isError(): boolean {
        const resource = this.customersListResource.get();
        return resource.type === 'error';
    }

    @computed public get isRequestMade(): boolean {
        return this.queryData !== null;
    }

    @computed public get errors(): string {
        const resource = this.customersListResource.get();
        if (resource.type === 'error') {
            return resource.message;
        }
        return '';
    }

    @computed public get customers(): Array<CustomerItemType> {
        const resource = this.customersListResource.get();
        if (resource.type === 'ready') {
            return resource.value;
        }

        return [];
    }

    @action public onSearch = async (queryData: Record<string, unknown>): Promise<void> => {
        this.queryData = queryData;
        this.isSearching = true;
        try {
            await this.customersListResource.refresh();
        } finally {
            this.isSearching = false;
        }
    };
}
